import { useEffect, useState } from "react";
import { AdminLayout } from "./Admin";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Mail, Bell } from "lucide-react";
import { toast } from "sonner";

type Prefs = Record<string, { email: boolean; in_app: boolean }>;

const EVENTS: { kind: string; label: string; description: string }[] = [
  { kind: "booking_created", label: "Booking created", description: "A new studio booking has been placed (before payment)." },
  { kind: "payment_succeeded", label: "Payment succeeded", description: "A booking or order payment has cleared." },
  { kind: "payment_failed", label: "Payment failed", description: "Stripe reported a failed payment intent." },
  { kind: "order_paid", label: "Order paid", description: "Shop order checkout completed." },
  { kind: "invoice_sent", label: "Invoice sent", description: "Booking invoice email was sent or resent." },
  { kind: "refund_completed", label: "Refund completed", description: "Full or partial refund processed successfully." },
  { kind: "refund_failed", label: "Refund failed", description: "Stripe refund.failed event received." },
  { kind: "contact_received", label: "Contact request", description: "Someone submitted the contact form." },
];

const defaults = (): Prefs => Object.fromEntries(EVENTS.map((e) => [e.kind, { email: true, in_app: true }]));

const AdminNotificationPreferences = () => {
  const [prefs, setPrefs] = useState<Prefs>(defaults());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("site_settings").select("notification_preferences").eq("id", 1).maybeSingle()
      .then(({ data }) => {
        const stored = ((data as any)?.notification_preferences ?? {}) as Prefs;
        setPrefs({ ...defaults(), ...stored });
        setLoading(false);
      });
  }, []);

  const toggle = (kind: string, channel: "email" | "in_app") => {
    setPrefs((p) => ({ ...p, [kind]: { ...p[kind], [channel]: !p[kind][channel] } }));
  };

  const save = async () => {
    setSaving(true);
    const { error } = await supabase.from("site_settings").update({ notification_preferences: prefs as any }).eq("id", 1);
    setSaving(false);
    if (error) toast.error(error.message);
    else toast.success("Preferences saved");
  };

  if (loading) return <AdminLayout title="NOTIFICATION PREFERENCES"><div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></AdminLayout>;

  return (
    <AdminLayout title="NOTIFICATION PREFERENCES">
      <p className="font-body text-muted-foreground mb-6 max-w-2xl">
        Choose which events trigger an email to the studio inbox and which appear in the in-app
        notifications bell. Disabling both stops the alert entirely.
      </p>
      <div className="bg-panel border border-border">
        <div className="grid grid-cols-[1fr_120px_120px] items-center px-4 py-3 border-b border-border text-xs font-mono uppercase tracking-widest text-muted-foreground">
          <div>Event</div>
          <div className="flex items-center gap-1 justify-center"><Mail className="w-3 h-3" /> Email</div>
          <div className="flex items-center gap-1 justify-center"><Bell className="w-3 h-3" /> In-app</div>
        </div>
        {EVENTS.map((e) => (
          <div key={e.kind} className="grid grid-cols-[1fr_120px_120px] items-center px-4 py-4 border-b border-border last:border-0">
            <div>
              <p className="font-body text-sm">{e.label}</p>
              <p className="text-xs text-muted-foreground">{e.description}</p>
              <p className="text-[10px] font-mono text-muted-foreground mt-1">{e.kind}</p>
            </div>
            <div className="flex justify-center"><Switch checked={prefs[e.kind]?.email ?? true} onCheckedChange={() => toggle(e.kind, "email")} /></div>
            <div className="flex justify-center"><Switch checked={prefs[e.kind]?.in_app ?? true} onCheckedChange={() => toggle(e.kind, "in_app")} /></div>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <Button onClick={save} disabled={saving} className="gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save preferences
        </Button>
      </div>
    </AdminLayout>
  );
};

export default AdminNotificationPreferences;